import React, { useState, useEffect } from 'react';
import { Fade, Reveal } from 'react-awesome-reveal';
import { Parallax, ParallaxProvider } from 'react-scroll-parallax';
import { useInView } from 'react-intersection-observer';
import { keyframes } from "@emotion/react";
import { WidgetsContext } from '../../components/widgets/core';
import _ from 'lodash';

const AnimationComponentView = ({ type, disabled, children, ...rest }) => {
  if (disabled) {
    return children;
  }

  const context = React.useContext(WidgetsContext);
  const storyboardType = _.get(context, 'storyboard.type', '');
  const fraction = storyboardType === 'INTERACTIVE_CONTENT' ? 0.5 : 0.2;

  var style = {
    height: "100%"
  };

  switch (type) {

    case 'fade': {
      const { delay, triggerOnce, duration } = rest;
      return (
        <FadeAnimation
          delay={delay}
          fraction={fraction}
          triggerOnce={triggerOnce}
          duration={duration}
          style={style}
        >
          {children}
        </FadeAnimation>
      )
    }

    case 'slide': {
      const { duration, delay, direction, distance, triggerOnce } = rest;
      return (
        <SlideAnimation
          delay={delay}
          direction={direction}
          fraction={fraction}
          distance={distance}
          triggerOnce={triggerOnce}
          duration={duration}
          style={style}
        >
          {children}
        </SlideAnimation>
      )
    }

    case 'spin': {
      const { duration, delay, direction, count, triggerOnce } = rest;
      return (
        <SpinAnimation
          delay={delay}
          direction={direction}
          fraction={fraction}
          count={count}
          triggerOnce={triggerOnce}
          duration={duration}
          style={style}
        >
          {children}
        </SpinAnimation>
      )
    }

    case 'bounce': {
      const { delay, triggerOnce, direction, duration } = rest;
      return (
        <BounceAnimation
          delay={delay}
          // direction={direction}
          fraction={fraction}
          triggerOnce={triggerOnce}
          duration={duration}
          style={style}
        >
          {children}
        </BounceAnimation>
      )
    }

    case 'arc': {
      const { delay, triggerOnce, direction, duration } = rest;
      return (
        <ArcAnimation
          delay={delay}
          direction={direction}
          fraction={fraction}
          triggerOnce={triggerOnce}
          duration={duration}
          style={style}
        >
          {children}
        </ArcAnimation>
      )
    }

    case 'flip': {
      const { delay, triggerOnce, direction, duration } = rest;
      return (
        <FlipAnimation
          delay={delay}
          fraction={fraction}
          direction={direction}
          triggerOnce={triggerOnce}
          duration={duration}
          style={style}
        >
          {children}
        </FlipAnimation>
      )
    }

    case 'expand': {
      const { delay, triggerOnce, duration } = rest;
      return (
        <ExpandAnimation
          delay={delay}
          triggerOnce={triggerOnce}
          fraction={fraction}
          duration={duration}
          style={style}
        >
          {children}
        </ExpandAnimation>
      )
    }

    case 'puffin': {
      const { delay, triggerOnce, duration } = rest;
      return (
        <PuffAnimation
          delay={delay}
          triggerOnce={triggerOnce}
          fraction={fraction}
          duration={duration}
          style={style}
        >
          {children}
        </PuffAnimation>
      )
    }

    case 'turn': {
      const { delay, triggerOnce, direction, duration } = rest;
      return (
        <TurnInAnimation
          delay={delay}
          triggerOnce={triggerOnce}
          fraction={fraction}
          direction={direction}
          duration={duration}
          style={style}
        >
          {children}
        </TurnInAnimation>
      )
    }

    case 'reveal': {
      const { delay, triggerOnce, direction, duration } = rest;
      return (
        <RevealAnimation
          delay={delay}
          triggerOnce={triggerOnce}
          fraction={fraction}
          direction={direction}
          duration={duration}
          style={style}
        >
          {children}
        </RevealAnimation>
      )
    }

    case 'parallax': {
      const { direction, speed, angle, reverse } = rest;

      return (
        <ParallaxAnimation
          type={type}
          direction={direction}
          speed={speed}
          angle={angle}
          reverse={reverse}
          style={style}
        >
          {children}
        </ParallaxAnimation>
      )
    }

  }

  return children;
}

const FadeAnimation = ({ duration, delay, fraction, children, triggerOnce, style }) => {
  return (
    <Fade fraction={fraction} triggerOnce={triggerOnce} duration={duration * 1000} delay={delay * 1000} style={style}>
      {children}
    </Fade>
  )
}

const SlideAnimation = ({ duration, triggerOnce, delay, direction, fraction, distance, children, style }) => {

  const { ref, inView, entry } = useInView({
    threshold: fraction,
    triggerOnce: triggerOnce
  });

  const animationVlaue = generateKeyFrameForSlide(direction, distance);

  return (
    <div ref={ref} style={style}>
      <div style={{
        transform: inView ? animationVlaue.to.transform : animationVlaue.from.transform,
        transition:  `transform ${ inView?duration:0}s  ease-out ${ inView?delay:0}s`,
        height: "100%"
      }}>
        {children}
      </div>
    </div>
  )
}

const SpinAnimation = ({ duration, delay, triggerOnce, fraction, direction, count, children, style }) => {
  return (
    <Reveal fraction={fraction} style={{ animationTimingFunction: "ease", ...style }} triggerOnce={triggerOnce} keyframes={generateKeyFrameForSpin(direction, count)} duration={duration * 1000} delay={delay * 1000}>
      {children}
    </Reveal>
  )
}

const BounceAnimation = ({ duration, delay, triggerOnce, fraction, children, style }) => {
  return (
    <Reveal fraction={fraction} style={{ animationTimingFunction: "ease", ...style }} triggerOnce={triggerOnce} keyframes={generateKeyFrameForBounce()} duration={duration * 1000} delay={delay * 1000}>
      {children}
    </Reveal>
  )
}

const ArcAnimation = ({ duration, delay, triggerOnce, fraction, direction, children, style }) => {
  const { ref, inView, entry } = useInView({
    threshold: fraction,
    triggerOnce: triggerOnce
  });

  const animationVlaue = generateKeyFrameForArc(direction);
  return (
    <div ref={ref} style={style}>
      <div style={{
        transform: inView ? animationVlaue.to.transform : animationVlaue.from.transform,
        scale: inView ? animationVlaue.to.scale : animationVlaue.from.scale,
        opacity: inView ? animationVlaue.to.opacity : animationVlaue.from.opacity,
        transition:  `all ${ inView?duration:0}s  ease-out ${ inView?delay:0}s`,
        height: "100%"
      }}>
        {children}
      </div>
    </div>
  )
}

const FlipAnimation = ({ duration, delay, triggerOnce, fraction, direction, children, style }) => {
  return (
    <Reveal fraction={fraction} style={{ animationTimingFunction: "ease", ...style }} triggerOnce={triggerOnce} keyframes={generateKeyFrameForFlip(direction)} duration={duration * 1000} delay={delay * 1000}>
      {children}
    </Reveal>
  )
}

const ExpandAnimation = ({ duration, delay, fraction, triggerOnce, children, style }) => {
  return (
    <Reveal fraction={fraction} style={{ animationTimingFunction: "ease", ...style }} triggerOnce={triggerOnce} keyframes={generateKeyFrameForExpand()} duration={duration * 1000} delay={delay * 1000}>
      {children}
    </Reveal>
  )
}

const PuffAnimation = ({ duration, delay, triggerOnce, fraction, children, style }) => {

  const { ref, inView, entry } = useInView({
    threshold: fraction,
    triggerOnce: triggerOnce
  });

  const animationVlaue = generateKeyFrameForPuff()

  return (
    <div ref={ref} style={style}>
      <div style={{
        transform: inView ? animationVlaue.to.transform : animationVlaue.from.transform,
        transition:  `transform ${ inView?duration:0}s  ease-out ${ inView?delay:0}s`,
        height: "100%"
      }}>
        {children}
      </div>
    </div>
  )
}

const TurnInAnimation = ({ duration, delay, triggerOnce, fraction, direction, children, style }) => {
  return (
    <Reveal fraction={fraction} style={{ animationTimingFunction: "ease", ...style }} triggerOnce={triggerOnce} keyframes={generateKeyFrameForTurnIn(direction)} duration={duration * 1000} delay={delay * 1000}>
      {children}
    </Reveal>
  )
}

const RevealAnimation = ({ duration, delay, fraction, triggerOnce, direction, children, style }) => {
  const { ref, inView, entry } = useInView({
    threshold: fraction,
    triggerOnce: triggerOnce
  });

  const animationVlaue = generateKeyFrameForReveal(direction);
  return (
    <div ref={ref} style={style}>
      <div style={{
        clipPath: inView ? animationVlaue.to : animationVlaue.from,
        transition:  `clip-path ${ inView?duration:0}s  ease-out ${ inView?delay:0}s`,
        height: "100%"

      }}>
        {children}
      </div>
    </div>
  )
}

const ParallaxAnimation = ({ direction, speed, angle, reverse, children }) => {
  return (
    <Parallax
      shouldAlwaysCompleteAnimation={false}
      translateX={getTranslateX(direction, speed, angle, reverse)}
      translateY={getTranslateY(direction, speed, angle, reverse)}
      style={{height:"100%"}}
    >
      {children}
    </Parallax>
  )
}

const generateKeyFrameForSlide = (direction, distance, count = 1) => {
  let animationKeyframe = null;

  switch (direction) {
    case 'right':
      animationKeyframe = {
        from: {
          transform: `translateX(-${distance}px)`
        },
        to: {
          transform: 'translateX(0)'
        }
      };
      break;

    case 'left':
      animationKeyframe = {
        from: {
          transform: `translateX(${distance}px)`
        },
        to: {
          transform: 'translateX(0)'
        }
      };
      break;

    case 'top':
      animationKeyframe = {
        from: {
          opacity: 1,
          transform: `translateY(${distance}px)`
        },
        to: {
          opacity: 1,
          transform: 'translateY(0)'
        }
      };
      break;

    case 'bottom':
      animationKeyframe = {
        from: {
          transform: `translateY(-${distance}px)`
        },
        to: {
          transform: 'translateY(0)'
        }
      };
      break;

    default:
      break;
  }

  return animationKeyframe;
};


const getTranslateX = (direction, speed, angle, reverse) => {
  let translate = null;

  var end = 100;
  var degree = 0;
  switch (direction) {
    case 'vertical':
      degree = 0;
      end = 0;
      break;

    case 'horizontal':
      degree = 360;
      break;

    case 'angle':
      degree = angle - 90;
      break;

    default:
      break;
  }

  var x = Number.parseFloat(Math.cos(Math.PI * 2 * degree / 360)).toFixed(2);
  const delta = end * x * speed;
  translate = reverse ? [`${delta}px`, `${-delta}px`] : [`${-delta}px`, `${delta}px`];

  return translate;
}

const getTranslateY = (direction, speed, angle, reverse) => {
  let translate = null;

  var end = 100;
  var degree = 0;
  switch (direction) {
    case 'vertical':
      degree = 270;
      break;

    case 'horizontal':
      degree = 0;
      end = 0;
      break;

    case 'angle':
      degree = angle - 90;
      break;

    default:
      break;
  }

  var y = Number.parseFloat(Math.sin(Math.PI * 2 * degree / 360)).toFixed(2);
  const delta = end * y * speed;
  translate = reverse ? [`${delta}px`, `${-delta}px`] : [`${-delta}px`, `${delta}px`];

  return translate;
}

const generateKeyFrameForSpin = (direction, count) => {
  let animationKeyframe = null;

  switch (direction) {
    case 'right':
      animationKeyframe = keyframes`
        from {
          opacity:0;
          transform:scale(0) rotate(0deg);
        }

        to {
          opacity:1;
          transform:scale(1) rotate(${360 * count}deg);
        }
      `;
      break;

    case 'left':
      animationKeyframe = keyframes`
        from {
          opacity:0;
          transform:scale(0) rotate(${360 * count}deg);
        }

        to {
          opacity:1;
          transform:scale(1) rotate(0deg);
        }
      `;
      break;

    default:
      break;
  }

  return animationKeyframe;
}

const generateKeyFrameForBounce = () => {

  let animationKeyframe = keyframes`
  from {
    opacity: 0;
    transform: scale(0.1);
  }
  60% {
    opacity: 1;
    transform: scale(1.2);
  }
  80% {
    transform: scale(0.9);
  }
  to {
    transform: scale(1);
  }`;

  return animationKeyframe;
}

const generateKeyFrameForArc = (direction) => {
  let animationValue = null;

  switch (direction) {
    case 'right':
      animationValue = {
        from: {
          opacity: 0,
          scale: 0.5,
          transform: 'perspective(1500px) rotateY(180deg) translateZ(300px)'
        },
        to: {
          opacity: 1,
          scale: 1,
          transform: 'perspective(1500px) rotateY(0deg) translateZ(0px)'
        }
      };
      break;

    case 'left':
      animationValue = {
        from: {
          opacity: 0,
          scale: 0.5,
          transform: 'perspective(1500px) rotateY(-180deg) translateZ(300px)'
        },
        to: {
          opacity: 1,
          scale: 1,
          transform: 'perspective(1500px) rotateY(0deg) translateZ(0px)'
        }
      };
      break;

    default:
      break;
  }

  return animationValue;
};


const generateKeyFrameForFlip = (direction) => {
  let animationKeyframe = null;

  switch (direction) {
    case 'right':
      animationKeyframe = keyframes`
      from {
        transform-origin: left;
        transform: rotateY(90deg);
      }
      to {
        transform: rotateY(0deg);
      }`;
      break;

    case 'left':
      animationKeyframe = keyframes`
      from {
        transform-origin: right;
        transform: rotateY(-90deg);
      }
      to {
        transform: rotateY(0deg);
      }
      `;
      break;

    case 'top':
      animationKeyframe = keyframes`
      from {
        transform-origin: bottom;
        transform: rotateX(90deg);
      }
      to {
        transform: rotateX(0deg);
      }`;
      break;

    case 'bottom':
      animationKeyframe = keyframes`
        from {
          transform-origin: top;
          transform: rotateX(-90deg);
        }
        to {
          transform: rotateX(0deg);
        }
        `;
      break;

    default:
      break;
  }

  return animationKeyframe;
}

const generateKeyFrameForExpand = () => {

  let animationKeyframe = keyframes`
  from {
    opacity: 0;
    transform: scale(0);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }`;

  return animationKeyframe;
}

const generateKeyFrameForPuff = () => {
  let animationKeyframe = {
    from: {
      transform: 'scale(2.5)'
    },
    to: {
      opacity: 1,
      transform: 'scale(1)'
    }
  };

  return animationKeyframe;
};

const generateKeyFrameForTurnIn = (direction) => {

  let animationKeyframe = null;

  switch (direction) {
    case 'right':
      animationKeyframe = keyframes`
      0% {
        opacity: 0;
        transform: translate(200%, -200%) rotate(45deg);
      }
      100% {
        opacity: 1;
        transform: translate(0, 0) rotate(0deg);
      }`;
      break;

    case 'left':
      animationKeyframe = keyframes`
      from {
        opacity: 0;
        transform: translate(-200%, -200%) rotate(-45deg);
      }
      to {
        opacity: 1;
        transform: translate(0, 0) rotate(0deg);
      }`;
      break;

    default:
      break;
  }


  return animationKeyframe;
}

const generateKeyFrameForReveal = (direction) => {

  let animationKeyframe = null;

  switch (direction) {
    case 'right':
      animationKeyframe = {
      from:'polygon(0 0, 0 0, 0% 100%, 0% 100%)',
      to :'polygon(0 0, 100% 0, 100% 100%, 0% 100%)'
    }
      break;

    case 'left':
      animationKeyframe = {
      from :'polygon(100% 0, 100% 0, 100% 100%, 100% 100%)',
      to :'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
    };
      break;


    case 'top':
      animationKeyframe = {
      from: 'polygon(0 100%, 100% 100%, 100% 100%, 0 100%)',
      to :'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
    }
      break;


    case 'bottom':
      animationKeyframe = {
      from :'polygon(0 0, 100% 0, 100% 0, 0 0)',
      to :'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
    };
      break;

    default:
      break;
  }


  return animationKeyframe;
}

const animationEquals = function (prevProps, nextProps) {
  const paths = ['preview'];
  const next = _.omit(nextProps, paths);
  const previous = _.omit(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

export const AnimationComponent = React.memo(AnimationComponentView, animationEquals);

export const ParallaxContainer = ({ scrollRef, children }) => {

  if (children) {
    return (
      <ParallaxProvider scrollContainer={scrollRef} >
        {children}
      </ParallaxProvider>
    )
  }

  return children;
}
